import _ from 'lodash';
import { Icon, Banner } from '@scale/scaleui';
import StepWithMaybeResponse from './StepWithMaybeResponse';
import { EditableChatMessage } from './EditableChatMessage';
import { usePinStore } from './hooks/usePinStore';
import { useChatStoreExtract } from '../Chat/hooks/ChatStore.hooks';
import { InteractionStepType } from '@scale/shared/types/chat';

interface PinnedItemsPanelProps {
  readOnly?: boolean;
}

export function PinnedItemsPanel({ readOnly }: PinnedItemsPanelProps) {
  const pinnedIndexes = usePinStore(state => state.pinnedIndexes);
  const isPinnedIndex = usePinStore(state => state.isPinnedIndex);
  const removePinnedIndex = usePinStore(state => state.removePinnedIndex);
  const isPinnedMessage = usePinStore(state => state.isPinnedMessage);
  const pinnedMessages = usePinStore(state => state.pinnedMessages);
  const removePinnedMessage = usePinStore(state => state.removePinnedMessage);
  const getPinnedMessage = usePinStore(state => state.getPinnedMessage);

  const stepParams = useChatStoreExtract(state => state.stepParams);
  const responses = useChatStoreExtract(state => state.responses);
  const config = useChatStoreExtract(state => state.config);

  const enableRTLTextPromptInput = (
    config.before.find(step => step.type === InteractionStepType.PromptInput) as any
  )?.params.enable_rtl_text;

  return (
    <div>
      {pinnedIndexes.length === 0 && _.size(pinnedMessages) === 0 && (
        <div className="p-2">
          <Banner
            variant="info"
            title="Pinning step items"
            subtitle="Pin step items to the sidebar by clicking the pin icon to the side of the step. Pinning an item will not make any changes to the chat responses."
            background="neutral"
          />
        </div>
      )}
      <div className="flex flex-col gap-4">
        {stepParams.map(step => (
          <>
            {isPinnedMessage(step.responseIndex) && (
              <div
                key={`side-drawer-prompt-${step.responseIndex}`}
                className="flex align-start gap-4 h-full items-center"
              >
                <div className="flex flex-col items-center">
                  <Icon
                    variant="primary"
                    className="border border-neutral-200 shadow-md"
                    icon="exchange"
                    onClick={() => removePinnedMessage(step.responseIndex)}
                    tooltip="Return the item to the main chat view"
                  />
                </div>
                <EditableChatMessage
                  className="w-full"
                  message={getPinnedMessage(step.responseIndex)}
                  readOnly={true}
                  referenceTexts={responses.at(step.responseIndex)?.context?.referenceTexts}
                  attachments={responses.at(step.responseIndex)?.context?.attachments}
                  enableRTLText={enableRTLTextPromptInput}
                  turnIndex={step.turn + 1}
                  responseIndex={step.responseIndex}
                  enablePinning={true}
                />
              </div>
            )}
            {!!responses.at(step.responseIndex) && isPinnedIndex(step.responseIndex) && (
              <div
                key={`side-drawer-step-${step.responseIndex}`}
                className="flex align-start gap-4 h-full items-center"
              >
                <div className="flex flex-col justify-center">
                  <Icon
                    variant="primary"
                    className="border border-neutral-200 shadow-md"
                    icon="exchange"
                    onClick={() => removePinnedIndex(step.responseIndex)}
                    tooltip="Return the item to the main chat view"
                  />
                </div>
                <StepWithMaybeResponse
                  responseIndex={step.responseIndex}
                  readOnly={readOnly}
                  isPinnedView={true}
                  disabledOpacity={1}
                />
              </div>
            )}
          </>
        ))}
      </div>
    </div>
  );
}
